import type {
  CreateBookingBody,
  RedeemCreditBookingBody,
  RequestAccessCodeBody,
  VerifyAccessCodeBody,
} from '@yoga-tool/shared';
import type {
  BookingStatusResponse,
  CreateBookingResponse,
  CreatePaymentResponse,
  ClassSession,
  CreditPassSummary,
  Product,
  RequestAccessCodeResponse,
  VerifyAccessCodeResponse,
  WidgetReturnResponse,
} from '../types';
import {
  bookingStatusResponseSchema,
  classSessionSchema,
  createBookingResponseSchema,
  createPaymentResponseSchema,
  creditPassSummarySchema,
  productSchema,
  requestAccessCodeResponseSchema,
  verifyAccessCodeResponseSchema,
  widgetReturnResponseSchema,
} from '../types';
import { z } from 'zod';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseJson<T>(
  response: Response,
  schema: z.ZodType<T>,
): Promise<T> {
  const text = await response.text();
  let payload: unknown = null;

  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      throw new ApiError('Ungültige Server-Antwort', response.status, text);
    }
  }

  if (!response.ok) {
    const message =
      typeof payload === 'object' &&
      payload !== null &&
      'message' in payload &&
      typeof (payload as { message: unknown }).message === 'string'
        ? (payload as { message: string }).message
        : `Anfrage fehlgeschlagen (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError('Unerwartetes Antwortformat', response.status, payload);
  }

  return parsed.data;
}

export function createApiClient(apiUrl: string, studioSlug: string) {
  const base = apiUrl.replace(/\/$/, '');
  const studioBase = `${base}/api/v1/public/studios/${encodeURIComponent(studioSlug)}`;

  return {
    async getSessions(): Promise<ClassSession[]> {
      const response = await fetch(`${studioBase}/sessions`);
      return parseJson(response, z.array(classSessionSchema));
    },

    async getProducts(): Promise<Product[]> {
      const response = await fetch(`${studioBase}/products`);
      return parseJson(response, z.array(productSchema));
    },

    async createBooking(body: CreateBookingBody): Promise<CreateBookingResponse> {
      const response = await fetch(`${studioBase}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return parseJson(response, createBookingResponseSchema);
    },

    async createPayment(
      bookingId: string,
      accessToken: string,
    ): Promise<CreatePaymentResponse> {
      const response = await fetch(`${base}/api/v1/public/bookings/${bookingId}/payment`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return parseJson(response, createPaymentResponseSchema);
    },

    async getBookingStatus(
      bookingId: string,
      accessToken: string,
    ): Promise<BookingStatusResponse> {
      const response = await fetch(`${base}/api/v1/public/bookings/${bookingId}/status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return parseJson(response, bookingStatusResponseSchema);
    },

    async getWidgetReturn(token: string): Promise<WidgetReturnResponse> {
      const response = await fetch(
        `${base}/api/v1/public/widget-return/${encodeURIComponent(token)}`,
      );
      return parseJson(response, widgetReturnResponseSchema);
    },

    async requestAccessCode(
      body: RequestAccessCodeBody,
    ): Promise<RequestAccessCodeResponse> {
      const response = await fetch(`${studioBase}/access/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return parseJson(response, requestAccessCodeResponseSchema);
    },

    async verifyAccessCode(
      body: VerifyAccessCodeBody,
    ): Promise<VerifyAccessCodeResponse> {
      const response = await fetch(`${studioBase}/access/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return parseJson(response, verifyAccessCodeResponseSchema);
    },

    async getCreditPasses(customerAccessToken: string): Promise<CreditPassSummary[]> {
      const response = await fetch(`${studioBase}/me/credit-passes`, {
        headers: {
          Authorization: `Bearer ${customerAccessToken}`,
        },
      });
      return parseJson(response, z.array(creditPassSummarySchema));
    },

    async redeemCreditBooking(
      customerAccessToken: string,
      body: RedeemCreditBookingBody,
    ): Promise<CreateBookingResponse> {
      const response = await fetch(`${studioBase}/bookings/with-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${customerAccessToken}`,
        },
        body: JSON.stringify(body),
      });
      return parseJson(response, createBookingResponseSchema);
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
